import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiPaths } from "../../api/paths";
import DataTable from "../../components/data-table/DataTable";
import Pagination from "../../components/pagination/Pagination";
import useData from "../../hooks/use-data";
import useDisclosure from "../../hooks/use-disclosure";
import usePagination from "../../hooks/use-pagination";
import { PaginatedApiResponseType } from "../../types/common";
import { ActionType, ColumnType } from "../../types/data-table";
import { StaffType } from "../../types/staff";
import DeleteStaff from "./delete-staff/DeleteStaff";
import ManageStaffStats from "./manage-staff-stats/ManageStaffStats";
import "./manage-staff.css";
import { useAppContext } from "../../context/app-context";

export default function ManageStaff() {
  const { t } = useTranslation(["manageStaff"]);
  const { role, user } = useAppContext();
  const { show, onShow, onHide } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState<StaffType>();
  const pagination = usePagination();
  const params = useMemo(
    () => ({
      params: {
        companyName: user?.attributes?.companyName
          ? user?.attributes?.companyName[0]
          : "",
        page: pagination.page,
        limit: pagination.limit,
      },
    }),
    [pagination.page, pagination.limit, role, user]
  );
  const { data, refetch, loading } = useData<
    PaginatedApiResponseType<StaffType[]>
  >(ApiPaths.staff, {
    options: params,
    onError: () => {
      toast(t("error.default", { ns: "common" }), {
        type: "error",
      });
    },
  });

  const columns: ColumnType[] = [
    {
      accessor: "username",
      header: t("table_head.name", { ns: "manageStaff" }),
      formatDisplay: (row) => {
        return `${row?.firstName} ${row?.lastName}`;
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "attributes.companyName",
      header: t("table_head.company_name", { ns: "manageStaff" }),
      formatDisplay: (row) => {
        return row?.attributes?.role
          ? row?.attributes?.role[0] === "individual"
            ? "Individual"
            : row?.attributes?.companyName
            ? row?.attributes?.companyName[0]
            : "-"
          : "-";
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "user_type",
      header: t("table_head.user_type", { ns: "manageStaff" }),
      formatDisplay: (row) => {
        return (
          <div>
            {row?.user_type === "individual"
              ? "Individual"
              : row?.user_type === "business-user"
              ? "Business User"
              : row?.user_type === "business-admin"
              ? "Bsiness Admin"
              : "-"}
          </div>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "email",
      header: t("table_head.email", { ns: "manageStaff" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "phone_no",
      header: t("table_head.phone_no", { ns: "manageStaff" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "status",
      header: t("table_head.status", { ns: "manageStaff" }),
      formatDisplay: (row) => {
        return (
          <button
            className={`status-button px-4 py-2 ${
              row?.attributes?.userStatus
                ? row?.attributes?.userStatus[0].toLowerCase()
                : ""
            }-status`}
          >
            {row?.attributes?.userStatus ? row?.attributes?.userStatus[0] : ""}
          </button>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
  ];

  const actions: ActionType[] = [
    {
      name: "edit",
      action: (row: StaffType) => {
        return (
          <Link
            className="text-decoration-none"
            to={`/manage-staff/edit/${row?.id}`}
          >
            {t("button.edit", { ns: "common" })}
          </Link>
        );
      },
    },
    {
      name: "delete",
      action: (row: StaffType) => {
        return (
          <button
            className="status-button rejected-status px-3 py-1"
            onClick={() => {
              setSelectedItem(row);
              onShow();
            }}
          >
            {t("button.delete", { ns: "common" })}
          </button>
        );
      },
    },
  ];
  return (
    <div className="p-3 pb-5 p-md-4">
      <DeleteStaff
        show={show}
        onHide={onHide}
        selectedItem={selectedItem}
        onSuccess={refetch}
      />
      <div className="rounded-2 bg-white p-4 pb-0 px-md-5 pb-md-3 mb-3 mb-md-4 box-shadow">
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 ">
          <h1 className="primary-title">
            {t("heading", { ns: "manageStaff" })}
          </h1>
        </div>
        <ManageStaffStats />
      </div>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <Link
          to="/manage-staff/add"
          className="primary-button text-uppercase  text-decoration-none"
        >
          {t("button.add", { ns: "manageStaff" })}
        </Link>
      </div>
      <div className="manage-staff-table box-shadow">
        <DataTable
          columns={columns}
          data={data?.data.map((ele: any) => ({
            ...ele,
            user_type: ele?.attributes?.role ? ele.attributes.role[0] : "-",
            phone_no: ele?.attributes?.mobile ? ele.attributes.mobile[0] : "-",
            designation: ele?.attributes?.designation
              ? ele?.attributes?.designation[0]
              : "-",
          }))}
          showSerial={true}
          actions={actions}
          loading={loading}
        />
      </div>
      <div className="mb-5">
        <Pagination total={data?.count ?? 10} pagination={pagination} />
      </div>
    </div>
  );
}
